import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  CircularProgress,
  Alert,
  Avatar,
  Paper,
  Button,
  Tooltip
} from '@mui/material';
import { MonetizationOn as MonetizationOnIcon, Add as AddIcon, FamilyRestroom as FamilyIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import LevelDisplay from '../components/LevelDisplay';

const KidSelectPage = () => {
  const { parent, kids, loading, selectKid } = useAuth();
  const [selecting, setSelecting] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!parent) {
      navigate('/login');
    }
  }, [parent, navigate]);

  const handleKidSelect = async (kidId: string) => {
    try {
      setSelecting(kidId);
      setError(null);
      await selectKid(kidId);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to select kid');
    } finally {
      setSelecting(null);
    }
  };

  const handleAddKid = () => {
    navigate('/manage');
  };

  if (loading || !parent) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ textAlign: 'center', mb: 4, mt: 4 }}>
        <Avatar
          src={parent.picture}
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'primary.main',
            mx: 'auto',
            mb: 2,
            boxShadow: 3
          }}
        >
          {parent.name.charAt(0).toUpperCase()}
        </Avatar>
        <Typography variant="h4" gutterBottom fontWeight={700} color="text.primary">
          Welcome, {parent.name}!
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Select a kid to view their progress
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {kids.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No kids added yet. Add your first kid to get started!
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddKid}
            sx={{ mt: 2 }}
          >
            Add Kid
          </Button>
        </Paper>
      ) : (
        <>
          <Grid container spacing={3} justifyContent="center">
            {kids.map((kid) => (
              <Grid item xs={12} sm={6} md={5} key={kid.id}>
                <Card
                  onClick={() => kid.id && handleKidSelect(kid.id)}
                  sx={{
                    cursor: 'pointer',
                    transition: 'all 0.2s ease-in-out',
                    border: '2px solid transparent',
                    opacity: selecting === kid.id ? 0.7 : 1,
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: 4,
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'center', py: 4 }}>
                    {selecting === kid.id ? (
                      <Box sx={{ py: 4 }}>
                        <CircularProgress />
                      </Box>
                    ) : (
                      <>
                        <Avatar sx={{
                          width: 80,
                          height: 80,
                          bgcolor: 'primary.light',
                          mx: 'auto',
                          mb: 2,
                          fontSize: '2rem',
                          fontWeight: 600
                        }}>
                          {kid.name.charAt(0).toUpperCase()}
                        </Avatar>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Tooltip title={`${kid.totalPoints} points`}>
                            <Typography variant="h5" fontWeight={600}>
                              {kid.name}
                            </Typography>
                          </Tooltip>
                          <Tooltip title={`${kid.totalPoints} points`}>
                            <LevelDisplay level={kid.level ?? 0} size="medium" />
                          </Tooltip>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <MonetizationOnIcon color="secondary" fontSize="small" />
                            <Typography variant="body2" color="text.secondary">
                              {kid.coins} coins
                            </Typography>
                          </Box>
                        </Box>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4, display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddKid}
            >
              Add Another Kid
            </Button>
            <Button
              variant="outlined"
              startIcon={<FamilyIcon />}
              onClick={() => navigate('/family')}
            >
              Family
            </Button>
          </Box>
        </>
      )}
    </Box>
  );
};

export default KidSelectPage;